"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeFromDeleted = exports.appendChildren = void 0;
const fp = require("fp-ts");
const monocle = require("monocle-ts");
const _ActionTypes = require("./actionTypes");
const types_1 = require("./types");
const appendChildren = (file, children) => {
    let hierarchy = [];
    if (children[file.id].length > 0) {
        hierarchy = hierarchy.concat(children[file.id]);
        children[file.id].forEach((f) => {
            hierarchy = hierarchy.concat((0, exports.appendChildren)(f, children));
        });
    }
    return hierarchy;
};
exports.appendChildren = appendChildren;
const removeFromDeleted = (arr, fileUuidsToRemove) => Object.keys(arr).reduce((acc, uuid) => {
    acc[uuid] = fileUuidsToRemove.includes(uuid) ? false : arr[uuid];
    return acc;
}, {});
exports.removeFromDeleted = removeFromDeleted;
function default_1(namespace) {
    const ActionTypes = _ActionTypes.default(namespace);
    const initialState = {
        checkingFiles: Object.assign({}, types_1.initialCheckingFiles),
        fileHistory: Object.assign({}, types_1.initialFileHistory)
    };
    //#region LENS
    const checkingLens = monocle.Lens.fromProp('checkingFiles');
    const checkingFilesStatusLens = monocle.Lens.fromProp('status');
    const checkingFilesFilesLens = monocle.Lens.fromProp('files');
    const currentFrameLens = monocle.Lens.fromPath(['fileHistory', 'currentFrame']);
    const fileHistoryLens = monocle.Lens.fromProp('fileHistory');
    const selectedFilesLens = monocle.Lens.fromProp('selectedFiles');
    //#endregion
    //#region REDUCER METHODS
    const methods = {};
    const flatten = (acc, item) => acc.concat(item);
    const findChildren = (uuid, files) => {
        const file = files.find((file) => file.uuid === uuid);
        if (!file)
            return [];
        if (file.type !== 'FOLDER')
            return [];
        const children = files
            .filter((f) => f.parent === file.id)
            .map((child) => [child.uuid].concat(findChildren(child.uuid, files)))
            .reduce(flatten, []);
        return children;
    };
    // Restore files that were deleted but were imported from cart again
    const restoreAlreadyRemoved = (state, action) => {
        const { deleted, colored, hardDeleted } = state.fileHistory.currentFrame;
        const { reimportedUuids, files, colorMetaId } = action.payload;
        const fileUuidsToRestore = reimportedUuids
            .map((uuid) => [uuid].concat(findChildren(uuid, files)))
            .reduce(flatten, []);
        const remainingDeleted = (0, exports.removeFromDeleted)(deleted, fileUuidsToRestore);
        const remainingHardDeleted = (0, exports.removeFromDeleted)(hardDeleted, fileUuidsToRestore);
        const restoredColors = colorMetaId
            ? Object.keys(colored).reduce((acc, item) => {
                const file = files.find((file) => file.uuid === item);
                const originalValue = file
                    ? file.customMeta[colorMetaId] === true
                    : false;
                acc[item] = fileUuidsToRestore.includes(item)
                    ? originalValue
                    : colored[item];
                return acc;
            }, {})
            : colored;
        return currentFrameLens.set({
            deleted: remainingDeleted,
            hardDeleted: remainingHardDeleted,
            colored: restoredColors
        });
    };
    methods[ActionTypes.PREVALIDATE_FILES_REQUEST] = (state, action) => fp.function.compose(checkingLens.compose(checkingFilesStatusLens).set('CHECKING'), checkingLens.compose(checkingFilesFilesLens).set([]))(state);
    methods[ActionTypes.PREVALIDATE_FILES_SUCCESS] = (state, action) => fp.function.compose(checkingLens.compose(checkingFilesFilesLens).set(action.payload.files), checkingLens
        .compose(checkingFilesStatusLens)
        .set(action.payload.valid === true ? 'VALID' : 'INVALID'), restoreAlreadyRemoved(state, action))(state);
    methods[ActionTypes.PREVALIDATE_FILES_FAILURE] = (state, action) => checkingLens.compose(checkingFilesStatusLens).set('FAILED')(state);
    methods[ActionTypes.PREVALIDATE_IMPORTED_FILES_REQUEST] = (state, action) => checkingLens.compose(checkingFilesStatusLens).set('CHECKING')(state);
    methods[ActionTypes.PREVALIDATE_IMPORTED_FILES_SUCCESS] = (state, action) => {
        const currentFiles = state.checkingFiles.files;
        const newFiles = action.payload.files;
        const combinedUniqueFiles = currentFiles.concat(newFiles.filter((f) => !currentFiles.find((cf) => cf.uuid === f.uuid)));
        return fp.function.flow(restoreAlreadyRemoved(state, action), checkingLens
            .compose(checkingFilesStatusLens)
            .set(action.payload.valid === true ? 'VALID' : 'INVALID'), checkingLens.compose(checkingFilesFilesLens).set(combinedUniqueFiles))(state);
    };
    methods[ActionTypes.PREVALIDATE_IMPORTED_FILES_FAILURE] = (state, action) => checkingLens.compose(checkingFilesStatusLens).set('FAILED')(state);
    methods[ActionTypes.SELECT_FILES] = (state, action) => fileHistoryLens.modify((fState) => selectedFilesLens.modify(() => {
        const parents = {};
        const childrens = {};
        const lookup = state.checkingFiles.files.reduce((prev, next) => {
            prev[next.id] = next.id;
            parents[next.id] = next.parent;
            if (!childrens[next.id]) {
                childrens[next.id] = [];
            }
            if (!childrens[next.parent]) {
                childrens[next.parent] = [];
            }
            if (!state.fileHistory.currentFrame.deleted[next.parentUuid] ===
                undefined ||
                (state.fileHistory.currentFrame.deleted[next.parentUuid] !==
                    undefined &&
                    state.fileHistory.currentFrame.deleted[next.parentUuid] ===
                        false) ||
                (state.fileHistory.currentFrame.deleted[next.uuid] === false &&
                    state.fileHistory.currentFrame.deleted[next.parentUuid] ===
                        false)) {
                childrens[next.parent].push(next);
            }
            return prev;
        }, {});
        const indexes = {};
        const files = state.checkingFiles.files.reduce((prev, next, i) => {
            prev[next.uuid] = next;
            indexes[next.id] = i;
            return prev;
        }, {});
        const selected = action.payload.uuids;
        action.payload.uuids.forEach((uuid) => {
            const file = files[uuid];
            if (!file) {
                return;
            }
            // completely delete file if no parent
            if (!lookup[file.parent]) {
                /* */
            }
            // delete any children
            const hierarchy = (0, exports.appendChildren)(file, childrens);
            hierarchy.forEach((f) => {
                selected.push(f.uuid);
            });
        });
        return Array.from(new Set(state.fileHistory.selectedFiles.concat(selected)));
    })(fState))(state);
    methods[ActionTypes.UNSELECT_FILES] = (state, action) => fileHistoryLens.modify((fState) => selectedFilesLens.modify(() => {
        const s = new Set(state.fileHistory.selectedFiles.concat(action.payload.uuids));
        for (const uuid of action.payload.uuids) {
            s.delete(uuid);
        }
        return Array.from(s);
    })(fState))(state);
    methods[ActionTypes.BROWSE] = (state, action) => {
        if (action.payload.browse === 1) {
            if (state.fileHistory.history.length === 1) {
                return state;
            }
            const poppedframe = state.fileHistory.history.pop();
            state.fileHistory.changes.push(poppedframe);
            return Object.assign(Object.assign({}, state), { fileHistory: Object.assign(Object.assign({}, state.fileHistory), { changes: state.fileHistory.changes, history: state.fileHistory.history, currentFrame: state.fileHistory.history[state.fileHistory.history.length - 1] }) });
        }
        else if (action.payload.browse === -1 &&
            state.fileHistory.changes.length) {
            const poppedframe = state.fileHistory.changes.pop();
            state.fileHistory.history.push(poppedframe);
            return Object.assign(Object.assign({}, state), { fileHistory: Object.assign(Object.assign({}, state.fileHistory), { changes: state.fileHistory.changes, histoty: state.fileHistory.history, currentFrame: state.fileHistory.history[state.fileHistory.history.length - 1] }) });
        }
        return state;
    };
    methods[ActionTypes.CHANGE_COLOR] = (state, action) => {
        const oldframe = state.fileHistory.currentFrame;
        const newframe = JSON.parse(JSON.stringify(oldframe));
        newframe.colored[action.payload.file.uuid] = action.payload.color;
        const cloned = state.fileHistory.history.slice();
        cloned.push(newframe);
        return Object.assign(Object.assign({}, state), { fileHistory: Object.assign(Object.assign({}, state.fileHistory), { history: cloned, currentFrame: newframe, changes: [] }) });
    };
    methods[ActionTypes.RESTORE_FILE] = (state, _action) => {
        const { fileHistory: { selectedFiles: fileUuidsToRestore, history, currentFrame: { deleted, hardDeleted, colored } } } = state;
        const foldersToRestore = state.checkingFiles.files
            .filter((x) => x.type === 'FOLDER' &&
            fileUuidsToRestore.includes(x.parentUuid || x.uuid))
            .map((x) => x.uuid);
        const childrenToRestore = state.checkingFiles.files.filter((x) => fileUuidsToRestore.includes(x.parentUuid) ||
            foldersToRestore.includes(x.parentUuid));
        const childrenUuidsToRestore = childrenToRestore.map((x) => x.uuid);
        const newDeleted = (0, exports.removeFromDeleted)(deleted, [
            ...fileUuidsToRestore,
            ...childrenUuidsToRestore
        ]);
        const newHardDeleted = (0, exports.removeFromDeleted)(hardDeleted, [
            ...fileUuidsToRestore,
            ...childrenUuidsToRestore
        ]);
        const newframe = {
            deleted: newDeleted,
            hardDeleted: newHardDeleted,
            colored: colored,
            selectedFiles: state.fileHistory.selectedFiles
                .concat(childrenUuidsToRestore)
                .concat(fileUuidsToRestore)
        };
        return Object.assign(Object.assign({}, state), { fileHistory: Object.assign(Object.assign({}, state.fileHistory), { history: history.concat(newframe), currentFrame: newframe, changes: [], selectedFiles: [...fileUuidsToRestore, ...childrenUuidsToRestore] }) });
    };
    methods[ActionTypes.REMOVE_FILE] = (state, action) => {
        const { fileHistory: { currentFrame: { deleted, hardDeleted, colored }, history }, checkingFiles: { files } } = state;
        const { payload: { uuids: fileUuidsToRemove } } = action;
        const remove = files
            .filter((x) => fileUuidsToRemove.includes(x.parentUuid) && deleted[x.uuid] !== false)
            .map((x) => x.uuid)
            .concat(...fileUuidsToRemove);
        const filesToDelete = files.filter((file) => remove.includes(file.uuid));
        const copyDeleted = (arr) => Object.keys(arr).reduce((acc, uuid) => {
            acc[uuid] = arr[uuid];
            return acc;
        }, {});
        const newDeleted = copyDeleted(deleted);
        const newHardDeleted = copyDeleted(hardDeleted);
        for (const uuid of remove) {
            newDeleted[uuid] = fileUuidsToRemove.includes(uuid);
        }
        for (const file of filesToDelete) {
            if (newDeleted[file.parentUuid] === true && file.type !== 'FOLDER') {
                newHardDeleted[file.uuid] = true;
                if (!state.fileHistory.currentFrame.deleted[file.uuid] &&
                    state.fileHistory.currentFrame.deleted[file.uuid] === false) {
                    newHardDeleted[file.uuid] = false;
                }
            }
        }
        const parentlessFilesToRemove = filesToDelete.filter((file) => files.find((f) => f.uuid === file.parentUuid) === undefined ||
            (newDeleted[file.uuid] && newDeleted[file.parentUuid]));
        for (const folder of parentlessFilesToRemove) {
            if (folder.type !== 'FOLDER') {
                newHardDeleted[folder.uuid] = true;
            }
        }
        const newframe = {
            deleted: newDeleted,
            hardDeleted: newHardDeleted,
            colored
        };
        return Object.assign(Object.assign({}, state), { fileHistory: Object.assign(Object.assign({}, state.fileHistory), { history: history.concat(newframe), currentFrame: newframe, changes: [] }) });
    };
    const reducer = (state = initialState, action) => {
        if (methods[action.type]) {
            return methods[action.type](state, action);
        }
        return state;
    };
    return { reducer, initialState, methods, ActionTypes };
}
exports.default = default_1;
//# sourceMappingURL=reducers.js.map